import { createFileRoute } from "@tanstack/react-router";

import { retainedQueryData } from "@sx/api-client";
import { deliveryReviewOnly, useLibraryAccess } from "../../../workspaces/library/access";
import { DeliveryReviewsPage } from "../../../workspaces/library/pages/DeliveryReviewsPage";
import { trimmedStringParam } from "../../../lib/url-state";
import { PublishedResultsPage } from "../../../workspaces/agents/PublishedResultsPage";
import { ProjectAccessRole } from "../../../lib/account";
import { useProject } from "../../../shell/project";
import { LibraryPage, type LibrarySearch } from "../../../workspaces/library/pages/LibraryPage";
import { parseEpisodesSearch } from "../../../workspaces/library/pages/episodes-search";
import { isShelf } from "../../../workspaces/library/kinds";
import { useCallback } from "react";
import { prefetchForProject } from "../../../lib/prefetch";

export interface LibraryBrowseSearch extends LibrarySearch {
  readonly result?: string;
  readonly published?: boolean;
  readonly delivery?: string;
  readonly reviews?: boolean;
}

export function parseLibraryBrowseSearch(
  search: Record<string, unknown>,
): LibraryBrowseSearch {
  const result = trimmedStringParam(search.result);
  const delivery = trimmedStringParam(search.delivery);
  return {
    result:
      result &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        result,
      )
        ? result
        : undefined,
    published: search.published === true || undefined,
    delivery: delivery && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(delivery) ? delivery : undefined,
    reviews: search.reviews === true || undefined,
    ...parseEpisodesSearch(search),
    tab: isShelf(search.tab) ? search.tab : search.tab === "agents" ? "agents" : undefined,
  };
}

export const Route = createFileRoute("/_console/p/$org/$project/library/")({
  validateSearch: parseLibraryBrowseSearch,
  loaderDeps: ({ search: { stock, task, purpose } }) => ({ stock, task, purpose }),
  loader: ({ context: { queryClient }, params, deps }) =>
    prefetchForProject(
      queryClient,
      params,
      Promise.all([
        import("../../../workspaces/library/api"),
        import("../../../workspaces/library/queries"),
      ]),
      ([{ createCatalogApi }, { libraryQueries }], projectId) => {
        const reads = libraryQueries(projectId, createCatalogApi(projectId));
        void queryClient.prefetchQuery(reads.overview);
        void queryClient.prefetchQuery(reads.episodeTotals(deps));
        void queryClient.prefetchQuery(reads.episodeMap);
      },
    ),
  component: LibraryOverviewRoute,
});

function LibraryOverviewRoute() {
  const search = Route.useSearch();
  const { result, published, delivery, reviews } = search;
  const navigate = Route.useNavigate();
  const onStateChange = useCallback(
    (patch: LibrarySearch) =>
      void navigate({ search: (current) => ({ ...current, ...patch }), replace: !("tab" in patch) }),
    [navigate],
  );
  const reviewOnly =
    useProject().selected?.managedAccessRole ===
    ProjectAccessRole.RESULT_REVIEWER;
  const access = useLibraryAccess(!reviewOnly && !result && !published);
  const decisionOnly = deliveryReviewOnly(retainedQueryData(access)?.scopes ?? []);
  return !reviewOnly && (decisionOnly || delivery || reviews) ? <DeliveryReviewsPage deliveryId={delivery} /> : reviewOnly || result || published ? (
    <PublishedResultsPage resultId={result} />
  ) : (
    <LibraryPage state={search} onStateChange={onStateChange} />
  );
}
